// src/components/Sidebar/ComponentLibrary/ComponentLibrary.tsx
// Updated to default to Reshot pack instead of "All"

import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Package, Plus, Loader2 } from 'lucide-react';
import { SearchBar } from './SearchBar';
import { IconPackSelector, IconPack } from './IconPackSelector';
import { useUiStateStore } from '@stores/uiStateStore';
import { iconPackManager } from '@/features/components/iconPacks';
import { IconRenderer } from '@/features/components/iconRenderer';
import type { IconDefinition } from '@/features/components/iconPacks/types';

export const ComponentLibrary: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');

  // Default to 'custom' pack (isometric icons)
  const [selectedPack, setSelectedPack] = useState('custom');

  // Loading state for lazy-loaded packs
  const [loadingPack, setLoadingPack] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const uiStateActions = useUiStateStore((state) => state.actions);

  // Load pack on-demand when selected
  useEffect(() => {
    const loadSelectedPack = async () => {
      // Check if pack is already loaded
      const pack = iconPackManager.getPack(selectedPack);
      if (pack) {
        return; // Already loaded
      }

      // Check if this pack exists (has a loader)
      const metadata = iconPackManager.getPackMetadata();
      const packExists = metadata.find((p) => p.id === selectedPack);

      if (!packExists) {
        return; // Not a valid pack
      }

      // Load the pack
      setLoadingPack(selectedPack);
      setLoadError(null);

      try {
        await iconPackManager.loadPack(selectedPack);
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Failed to load pack';
        setLoadError(errorMsg);
        console.error(`Failed to load pack ${selectedPack}:`, error);
      } finally {
        setLoadingPack(null);
      }
    };

    loadSelectedPack();
  }, [selectedPack]);

  // Get all icons from icon pack manager (will re-run when packs are loaded)
  const allIcons = useMemo(() => {
    return iconPackManager.getAllIcons();
  }, [loadingPack]); // Re-fetch when loading state changes

  // Get icon pack metadata for selector
  const iconPacks: IconPack[] = useMemo(() => {
    const metadata = iconPackManager.getPackMetadata();

    return [
      // {
      //   id: 'all',
      //   name: 'All',
      //   count: allIcons.length
      // },
      ...metadata.map((pack) => ({
        id: pack.id,
        name: pack.name,
        count: pack.count
      }))
    ];
  }, [allIcons.length]);

  // Filter icons based on search and selected pack
  const filteredIcons = useMemo(() => {
    let filtered = allIcons;

    // Filter by pack
    if (selectedPack !== 'all') {
      filtered = filtered.filter((icon) => icon.pack === selectedPack);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(
        (icon) =>
          icon.name.toLowerCase().includes(query) ||
          icon.category.toLowerCase().includes(query) ||
          icon.keywords?.some((keyword) =>
            keyword.toLowerCase().includes(query)
          )
      );
    }

    return filtered;
  }, [searchQuery, selectedPack, allIcons]);

  // Handle icon selection for placing on canvas
  const handleIconClick = useCallback(
    (icon: IconDefinition) => {
      // Set mode to place icon with the selected icon ID
      uiStateActions.setMode({
        type: 'PLACE_ICON',
        showCursor: true,
        id: icon.id
      });
    },
    [uiStateActions]
  );

  const handleIconDragStart = useCallback(
    (event: React.DragEvent, icon: IconDefinition) => {
      event.dataTransfer.effectAllowed = 'copy';
      event.dataTransfer.setData('application/flowi-icon', icon.id);
      event.dataTransfer.setData('text/plain', icon.id);
    },
    []
  );

  // Future feature: Custom icon upload
  // Will allow users to upload their own SVG/PNG icons to use in diagrams
  const handleUploadCustomIcon = () => {
    // Placeholder for future implementation
    // Will include: file picker, validation, upload to storage, icon registration
  };

  return (
    <div className="flex h-full flex-col overflow-hidden bg-white dark:bg-slate-900">
      {/* Search Bar */}
      <div className="flex-shrink-0 border-b border-gray-200 p-4 dark:border-slate-700">
        <SearchBar
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search icons..."
        />
      </div>

      {/* Icon Pack Selector */}
      <div className="flex-shrink-0 border-b border-gray-200 dark:border-slate-700">
        <IconPackSelector
          packs={iconPacks}
          selectedPack={selectedPack}
          onSelectPack={setSelectedPack}
        />
      </div>

      {/* Icons Grid */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="mb-2 flex items-center justify-between">
          <h4 className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
            Available Icons ({filteredIcons.length})
          </h4>
        </div>

        {/* Loading State */}
        {loadingPack && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Loader2 className="mb-3 h-12 w-12 animate-spin text-blue-500" />
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Loading {loadingPack} icons...
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              This may take a moment
            </p>
          </div>
        )}

        {/* Error State */}
        {loadError && !loadingPack && (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Package className="mb-2 h-12 w-12 text-red-400" />
            <p className="text-sm font-medium text-red-600 dark:text-red-400">
              Failed to load icons
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {loadError}
            </p>
          </div>
        )}

        {/* Grid of icon cards */}
        {!loadingPack && !loadError && (
          <div className="grid grid-cols-4 gap-2">
            {filteredIcons.map((icon) => (
              <div
                key={icon.id}
                onMouseDown={() => handleIconClick(icon)}
                onClick={() => handleIconClick(icon)}
                onDragStart={(event) => handleIconDragStart(event, icon)}
                draggable
                title={icon.name}
                className="group flex cursor-grab flex-col items-center justify-center gap-1 rounded-lg bg-gray-50 p-2 transition select-none hover:bg-gray-100 dark:bg-slate-800 dark:hover:bg-slate-700"
              >
                {/* Use IconRenderer to render icon */}
                <div className="pointer-events-none flex h-10 w-10 items-center justify-center">
                  <IconRenderer icon={icon} size={icon.isIsometric ? 40 : 32} />
                </div>
                <span className="pointer-events-none w-full truncate text-center text-[0.625rem] leading-tight text-gray-700 dark:text-gray-300">
                  {icon.name}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loadingPack && !loadError && filteredIcons.length === 0 && (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Package className="mb-2 h-12 w-12 text-gray-400" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No icons found
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              Try adjusting your search or pack filter
            </p>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex-shrink-0 border-t border-gray-200 p-4 dark:border-slate-700">
        <button
          disabled
          title="Upload Custom Icon (Coming Soon)"
          onClick={handleUploadCustomIcon}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700  disabled:opacity-40 disabled:cursor-not-allowed "
        >
          <Plus size={16} />
          Upload Icon (Coming Soon)
        </button>
      </div>
    </div>
  );
};
